import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "./api";

/**
 * Bilingual (EN / हिन्दी) language provider.
 *
 * Two responsibilities:
 *   1. `t(key)` — hand-written translations for high-visibility chrome
 *      (navbar, footer, primary CTAs) — quality-controlled, instant.
 *   2. `translate(texts)` — async batch translation for arbitrary DOM
 *      strings via the backend `/api/translate-batch` endpoint (backed
 *      by Claude Sonnet 4.6 + a persistent MongoDB cache). Used by
 *      `AutoTranslator` to swap every English text node on the page
 *      to Hindi without touching component code.
 *
 * Selection persists to localStorage AND to a `?lang=hi` URL query
 * parameter so a returning visitor keeps their choice and shared
 * links show the correct language.
 */

const STORE_KEY = "site-lang";
const SUPPORTED = new Set(["en", "hi"]);

const dict = {
  en: {
    "nav.home": "Home",
    "nav.about": "About",
    "nav.expertise": "Expertise",
    "nav.content_hub": "Content Hub",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.contact": "Contact",
    "nav.guides": "Guides",
    "cta.book": "Book Appointment",
    "cta.book_short": "Book",
    "cta.whatsapp": "WhatsApp",
    "cta.call": "Call",
    "cta.more": "Read more",
    "cta.download": "Download",
    "footer.serving": "Serving",
    "footer.districts": "Districts",
    "footer.varanasi": "Varanasi",
    "footer.explore": "Explore",
    "footer.visit": "Visit Us",
    "lang.toggle": "हिन्दी",
    "lang.aria": "Switch language",
  },
  hi: {
    "nav.home": "मुख्य पृष्ठ",
    "nav.about": "परिचय",
    "nav.expertise": "विशेषज्ञता",
    "nav.content_hub": "सामग्री केंद्र",
    "nav.blog": "ब्लॉग",
    "nav.faq": "प्रश्नोत्तर",
    "nav.contact": "संपर्क",
    "nav.guides": "पुस्तिका",
    "cta.book": "अपॉइंटमेंट लें",
    "cta.book_short": "बुक करें",
    "cta.whatsapp": "व्हाट्सएप",
    "cta.call": "कॉल",
    "cta.more": "और पढ़ें",
    "cta.download": "डाउनलोड",
    "footer.serving": "सेवा क्षेत्र",
    "footer.districts": "जिले",
    "footer.varanasi": "वाराणसी",
    "footer.explore": "अन्वेषण",
    "footer.visit": "मिलने आयें",
    "lang.toggle": "EN",
    "lang.aria": "भाषा बदलें",
  },
};

// Per-session in-memory memo layered above the Mongo cache so repeated
// strings on the same page don't hit the network at all.
const memo = new Map(); // key = `${lang}::${text}` -> translated string

const LangContext = createContext({
  lang: "en",
  t: (k) => k,
  setLang: () => {},
  translate: async (texts) => texts,
});

const readInitial = () => {
  if (typeof window === "undefined") return "en";
  try {
    const u = new URL(window.location.href);
    const q = (u.searchParams.get("lang") || "").toLowerCase();
    if (SUPPORTED.has(q)) return q;
    const s = (window.localStorage.getItem(STORE_KEY) || "").toLowerCase();
    if (SUPPORTED.has(s)) return s;
  } catch (_) {}
  return "en";
};

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => readInitial());

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "hi" ? "hi-IN" : "en-IN";
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.has(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORE_KEY, next);
      const u = new URL(window.location.href);
      if (next === "en") u.searchParams.delete("lang");
      else u.searchParams.set("lang", next);
      window.history.replaceState({}, "", u.toString());
    } catch (_) {}
  }, []);

  const t = useCallback((key) => dict[lang]?.[key] ?? dict.en[key] ?? key, [lang]);

  const translate = useCallback(async (texts) => {
    if (lang === "en" || !Array.isArray(texts) || !texts.length) return texts || [];
    const uniq = Array.from(new Set(texts.filter((s) => typeof s === "string" && s.trim())));
    const missing = uniq.filter((s) => !memo.has(`${lang}::${s}`));
    if (missing.length) {
      try {
        const r = await api.post("/translate-batch", { lang, texts: missing });
        const out = Array.isArray(r?.data?.translations) ? r.data.translations : [];
        missing.forEach((src, i) => memo.set(`${lang}::${src}`, out[i] ?? src));
      } catch (_e) {
        // Graceful degradation — leave English if the LLM/network fails.
        missing.forEach((src) => memo.set(`${lang}::${src}`, src));
      }
    }
    return texts.map((s) => (typeof s === "string" ? memo.get(`${lang}::${s}`) ?? s : s));
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, translate }), [lang, setLang, t, translate]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

